<template>
	<div>
		<div class="container">
			<div class="page">
				<!-- Loading State -->
				<div v-if="isLoading" class="text-center py-5">
					<div class="spinner-border text-primary" role="status">
						<span class="sr-only">Loading...</span>
					</div>
					<p class="mt-3 text-muted">Memuat data produk...</p>
				</div>

				<!-- Error State -->
				<div v-else-if="error" class="alert alert-danger" role="alert">
					<div class="d-flex align-items-center">
						<i class="material-icons me-2">error</i>
						<div>
							<h6 class="alert-heading mb-1">Terjadi Kesalahan</h6>
							<p class="mb-0">{{ error }}</p>
						</div>
					</div>
				</div>

				<!-- Product Info and Shop Selection -->
				<div v-else-if="product">
					<!-- Wizard Step Indicator -->
					<div class="wizard-steps mb-4">
						<div class="d-flex justify-content-between align-items-center">
							<div class="step-item text-center flex-fill"
								:class="{ 'active': currentStep === 1, 'completed': currentStep > 1 }">
								<div class="step-circle mx-auto mb-2">
									<span v-if="currentStep > 1">✓</span>
									<span v-else>1</span>
								</div>
								<div class="step-label">
									<strong>Atur Harga</strong>
								</div>
							</div>
							<div class="step-connector flex-fill"></div>
							<div class="step-item text-center flex-fill"
								:class="{ 'active': currentStep === 2, 'completed': currentStep > 2 }">
								<div class="step-circle mx-auto mb-2">
									<span v-if="currentStep > 2">✓</span>
									<span v-else>2</span>
								</div>
								<div class="step-label">
									<strong>Pilih Toko</strong>
								</div>
							</div>
						</div>
					</div>

					<!-- Product Header -->
					<div class="row mb-4">

						<!-- Main Content (Dynamic based on step) -->
						<div class="col-md-8">
							<!-- Step 1: Price Configuration -->
							<div v-if="currentStep === 1" class="card">
								<div class="card-body">
									<div class="d-flex justify-content-between align-items-center border-bottom pb-4 mb-4">
										<div>
											<h5 class="mb-1">Atur Harga Jual</h5>
											<p class="text-muted small mb-0">Tentukan margin keuntungan untuk setiap varian.</p>
										</div>
									</div>

									<!-- Product has variations -->
									<div v-if="hasProductVariations">
										<!-- Bulk Price Setting -->
										<div class="alert alert-info mb-4">
											<div class="d-flex flex-column flex-sm-row align-items-sm-end gap-3">
												<div class="flex-fill mr-3">
													<label class="small font-weight-bold mb-2">
														<i class="material-icons mr-2" style="font-size: 16px;">edit</i>
														Terapkan Harga Massal
													</label>
													<div class="input-group">
														<div class="input-group-prepend">
															<span class="input-group-text">Rp</span>
														</div>
														<input type="text"
															v-model="priceConfig.bulkPrice"
															placeholder="Contoh: 95.000"
															class="form-control">
													</div>
												</div>
												<button
													@click="applyBulkPrice"
													:disabled="!priceConfig.bulkPrice"
													class="btn btn-primary">
													Terapkan ke Semua
												</button>
											</div>
										</div>

										<!-- Variations Table -->
										<div class="table-responsive">
											<table class="table table-hover">
												<thead class="thead-light">
													<tr>
														<th>Nama Varian</th>
														<th>Modal</th>
														<th class="text-right">Rekomendasi</th>
														<th>Harga Jual Kamu</th>
													</tr>
												</thead>
												<tbody>
													<tr v-for="(variation, index) in product.variations.prices" :key="index">
														<td>
															<div class="font-weight-medium">{{ variation.name }}</div>
														</td>
														<td>
															<span class="badge badge-secondary">
																{{ formatMoney(variation.cogs) }}
															</span>
														</td>
														<td class="text-right">
															<span class="badge badge-success">
																{{ formatMoney(getVariationRecommendedPrice(variation)) }}
															</span>
														</td>
														<td>
															<div class="input-group" :class="{ 'is-invalid': !isVariationPriceValid(variation) }">
																<div class="input-group-prepend">
																	<span class="input-group-text">Rp</span>
																</div>
																<input type="text"
																	v-model="variation.selling_price"
																	class="form-control"
																	:class="{ 'is-invalid': !isVariationPriceValid(variation) }"
																	placeholder="0">
															</div>
															<small v-if="!isVariationPriceValid(variation)" class="text-danger">
																Harga jual tidak boleh kurang dari {{ formatMoney(getVariationRecommendedPrice(variation)) }}
															</small>
														</td>
													</tr>
												</tbody>
											</table>
										</div>
									</div>

									<!-- Product without variations -->
									<div v-else>
										<div class="p-4">
											<div class="row justify-content-center">
												<div class="col-md-8">
													<div class="form-group">
														<label class="form-label">Modal</label>
														<div class="form-control-plaintext">
															<span class="badge badge-secondary">
																{{ formatMoney(product.price) }}
															</span>
														</div>
													</div>
													<div class="form-group">
														<label class="form-label">Rekomendasi</label>
														<div class="form-control-plaintext">
															<span class="badge badge-success">
																{{ formatMarketplacePrice(product) }}
															</span>
														</div>
													</div>
													<div class="form-group">
														<label class="form-label">Harga Jual</label>
														<div class="input-group" :class="{ 'is-invalid': !isSingleProductPriceValid() }">
															<div class="input-group-prepend">
																<span class="input-group-text">Rp</span>
															</div>
															<input type="text"
																v-model="priceConfig.sellingPrice"
																:class="{ 'is-invalid': !isSingleProductPriceValid() }"
																placeholder="Masukkan harga jual"
																class="form-control">
														</div>
														<small v-if="!isSingleProductPriceValid()" class="text-danger">
															Harga jual tidak boleh kurang dari {{ formatMarketplacePrice(product) }}
														</small>
													</div>
												</div>
											</div>
										</div>
									</div>
								</div>
							</div>

							<!-- Step 2: Shop Selection -->
							<div v-if="currentStep === 2">
								<div  v-if="hasSelectedShopsWithInvalidAddress" class="card mb-3">
									<div class="card-body">
										<h5 class="card-title mb-4 d-flex align-items-center">
											<i class="material-icons text-warning mr-2">warning</i>
											<span>Alamat toko perlu diperbarui</span>
										</h5>

										<!-- Supplier Address Warning -->
										<p>Toko yang Anda pilih perlu mengubah alamat sesuai dengan alamat supplier:</p>
										<div class="supplier-address-details">
											<div class="supplier-address-row">
												<strong>Provinsi:</strong> {{ supplierAddress.province }}
											</div>
											<div class="supplier-address-row">
												<strong>Kota:</strong> {{ supplierAddress.city }}
											</div>
											<div class="supplier-address-row">
												<strong>Kecamatan:</strong> {{ supplierAddress.subdistrict }}
											</div>
											<div class="supplier-address-row">
												<strong>Alamat:</strong>
												<div class="supplier-address__copy-container">
													<span class="supplier-address__address">{{ supplierAddress.address
														}}</span>
													<button type="button"
														class="btn btn-sm btn-outline-secondary supplier-address__copy"
														@click="copyAddress(supplierAddress.address)"
														title="Copy address">
														<i class="material-icons">content_copy</i>
													</button>
												</div>
											</div>
											<div class="supplier-address-row">
												<strong>Kode Pos:</strong> {{ supplierAddress.zip }}
											</div>
										</div>
										<p class="supplier-address-note">
											Silakan ubah alamat toko Anda sesuai dengan format di atas, kemudian klik
											tombol "Saya sudah mengubah alamat" di bawah.
										</p>
									</div>
								</div>
								<div class="card mb-3">
									<div class="card-body">
										<h5 class="card-title mb-4">Pilih Toko</h5>

										<!-- Loading Shops -->
										<div v-if="isFetchingShops" class="text-center py-4 text-muted">
											<i class="material-icons spin me-2">refresh</i>
											Mengambil daftar toko...
										</div>

										<!-- Shop List -->
										<div v-else>
											<div v-if="shopeeShops.length" class="shops-list">
												<div v-for="shop in shopeeShops" :key="shop._id" class="shop-item"
													:class="{ 'shop-item--disabled': !isShopAvailable(shop) }">
													<div class="shop-item__checkbox">
														<input type="checkbox" :id="`shop-${shop._id}`" :value="shop.shop_id"
															v-model="selectedShopIds" :disabled="!isShopAvailable(shop)">
													</div>
													<label :for="`shop-${shop._id}`" class="shop-item__content">
														<div class="shop-item__info">
															<div class="shop-item__body">
																<i class="fi fi-rr-shop shop-icon mt-1 mx-2"></i>
																<span class="shop-item__name">{{ shop.store_label }}</span>
																<span
																	v-if="!shop.is_valid_address && selectedShopIds.includes(shop.shop_id)"
																	class="badge badge-warning">
																	Alamat tidak valid
																</span>
																<span v-else-if="isShopAlreadyAdded(shop)"
																	class="badge badge-success">
																	Ditambahkan
																</span>
																<span v-else-if="!isShopAvailable(shop)"
																	class="badge badge-secondary">
																	Sudah terhubung ke supplier lain
																</span>
															</div>
														</div>
													</label>
												</div>
											</div>
											<div v-else class="store-selection__empty">
												<p>Belum ada toko yang terhubung.</p>
												<button type="button" class="btn btn-outline-primary"
													@click="goToMarketplaceSettings">
													Tambahkan Toko
												</button>
											</div>
										</div>

										</div>
								</div>
							</div>
						</div>
						<div class="col-md-4">
							<div class="card mb-3">
								<!-- Product Image -->
								<img :src="productImage" :alt="product.name"
									class="card-img-top" style="height: 200px; object-fit: cover;">
								<!-- Product Info -->
								<div class="card-body">
									<h5 class="mb-2">{{ product.name }}</h5>
									<p class="text-muted mb-0">Kode: {{ product.code }}</p>
								</div>
							</div>


							<!-- Action Buttons Card -->
							<div class="card">
								<div class="card-body">
									<h6 class="card-title mb-3">Aksi</h6>
									<div class="shop-actions">
										<!-- Back Button -->
										<button v-if="showBackButton" type="button"
											class="btn btn-outline-secondary w-100 mb-2" :disabled="isSubmitting"
											@click="handleBack">
											<i class="material-icons">arrow_back</i>
											Kembali
										</button>

										<!-- Refresh Address Button (only in step 2) -->
										<button v-if="currentStep === 2 && hasSelectedShopsWithInvalidAddress" type="button"
											class="btn btn-outline-warning w-100 mb-2" :disabled="isSubmitting"
											@click="refreshShops">
											<i class="material-icons">refresh</i>
											Saya sudah mengubah alamat
										</button>

										<!-- Continue/Submit Button -->
										<button type="button" class="btn btn-primary w-100"
											:disabled="isSubmitting || (currentStep === 1 ? !canContinueStep1 : !canSubmitWizard)"
											:class="{ 'is-loading': isSubmitting }" @click="handleContinue">
											<span v-if="isSubmitting" class="spinner-border spinner-border-sm mr-1"
												role="status" aria-hidden="true"></span>
											{{ isSubmitting ? 'Memproses...' : continueButtonText }}
										</button>
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>

		<!-- Loading Modal -->
		<LoadingAddProduct
			:isVisible="showLoadingModal"
			:isLoading="isSubmitting"
			@close="closeLoadingModal"
		/>
	</div>

</template>

<script>
import productsApi from '@/api/products';
import { checkSupplier, addProduct } from '@/api/marketplace-shopee';
import {
	getDropshipFinalPrice,
	getDropshipVariationFinalPrice,
	getMarketplaceFinalPrice,
	formatMoney,
} from '@/lib/helper';
import LoadingAddProduct from '@/components/marketplace/LoadingAddProduct.vue';

export default {
	name: 'AddToMarketplacePage',
	components: {
		LoadingAddProduct,
	},
	data() {
		return {
			product: null,
			isLoading: false,
			error: null,
			showLoadingModal: false,
			// Wizard state
			currentStep: 1,
			totalSteps: 2,
			// Step 1: Price configuration
			priceConfig: {
				sellingPrice: null,
				basePrice: null,
				percentageIncrease: null,
				bulkPrice: null,
			},
			// Shop related data
			shopeeShops: [],
			isFetchingShops: false,
			selectedShopIds: [],
			isSubmitting: false,
			productAddedStatus: null,
			isFetchingProductStatus: false,
			supplierAddress: {},
		};
	},
	computed: {
		currentChannel() {
			return this.$route.params.channel;
		},
		currentProductSlug() {
			return this.$route.params.productSlug;
		},
		productImage() {
			return this.product?.picture ?? this.defaultImage;
		},
		productVariations() {
			return this.product?.variations ?? [];
		},
		hasProductVariations() {
			return this.product && this.product.type === 'variable' && this.productVariations && this.productVariations.prices && this.productVariations.prices.length > 0;
		},
		// Wizard computed properties
		stepTitle() {
			if (this.currentStep === 1) return 'Atur Harga';
			return 'Pilih Toko';
		},
		canContinueStep1() {
			if (this.hasProductVariations) {
				return this.product.variations.prices.every((variation) => {
					const sellingPrice = parseFloat(variation.selling_price) || 0;
					const recommendedPrice = this.getVariationRecommendedPrice(variation);
					return sellingPrice > 0 && sellingPrice >= recommendedPrice;
				});
			}
			const sellingPrice = parseFloat(this.priceConfig.sellingPrice) || 0;
			const recommendedPrice = getMarketplaceFinalPrice(this.product, [], 1);
			return sellingPrice > 0 && sellingPrice >= recommendedPrice;
		},
		canSubmitWizard() {
			return this.currentStep === 2
				&& this.selectedShopIds.length > 0
				&& this.getProductId()
				&& !this.hasSelectedShopsWithInvalidAddress;
		},
		showBackButton() {
			return this.currentStep > 1;
		},
		continueButtonText() {
			if (this.currentStep === 1) return 'Lanjut';
			return 'Kirim Produk';
		},
		addedShopeeShopIds() {
			if (!this.productAddedStatus || !this.productAddedStatus.sales_channels || !this.productAddedStatus.sales_channels.shopee) {
				return [];
			}
			return this.productAddedStatus.sales_channels.shopee;
		},
		hasSelectedShopsWithInvalidAddress() {
			if (!this.selectedShopIds || this.selectedShopIds.length === 0) {
				return false;
			}
			return this.selectedShopIds.some((shopId) => {
				const shop = this.shopeeShops.find(s => s.shop_id === shopId);
				return shop && !shop.is_valid_address;
			});
		},
		canSubmit() {
			return this.selectedShopIds.length > 0
				&& this.getProductId()
				&& !this.hasSelectedShopsWithInvalidAddress;
		},
	},
	async created() {
		await this.loadProduct();
		if (this.product) {
			this.initializePriceConfig();
			await this.fetchProductAddedStatus();
			await this.fetchShops();
		}
	},
	methods: {
		getDropshipFinalPrice,
		getDropshipVariationFinalPrice,
		formatMoney,
		// Wizard methods
		initializePriceConfig() {
			if (this.product) {
				// Use default dropship price as initial value
				const defaultPrice = getMarketplaceFinalPrice(this.product, [], 1);
				this.priceConfig.basePrice = this.product.price;
				this.priceConfig.sellingPrice = defaultPrice;

				// Initialize variation prices with recommended prices
				if (this.hasProductVariations && this.product.variations.prices) {
					this.product.variations.prices.forEach((variation) => {
						const recommendedPrice = this.getVariationRecommendedPrice(variation);
						this.$set(variation, 'selling_price', recommendedPrice);
					});
				}
			}
		},

		nextStep() {
			if (this.currentStep < this.totalSteps) {
				this.currentStep++;
			}
		},

		prevStep() {
			if (this.currentStep > 1) {
				this.currentStep--;
			}
		},

		handleContinue() {
			if (this.currentStep === 1) {
				if (this.canContinueStep1) {
					this.nextStep();
				}
			} else {
				this.submitProduct();
			}
		},

		handleBack() {
			this.prevStep();
		},

		// Variation methods
		getVariationStock(variation) {
			return variation.stock ? variation.stock.quantity : 0;
		},

		getVariationRecommendedPrice(variation) {
			// Use dropship variation final price with commission
			const dropshipVariations = getDropshipVariationFinalPrice(this.product, false, true, false);
			const variationPrice = dropshipVariations.prices.find(v => v.name === variation.name);
			return variationPrice ? variationPrice.price : getDropshipFinalPrice(this.product, [], 1, true, false);
		},

		isVariationPriceValid(variation) {
			const sellingPrice = parseFloat(variation.selling_price) || 0;
			const recommendedPrice = this.getVariationRecommendedPrice(variation);
			return sellingPrice > 0 && sellingPrice >= recommendedPrice;
		},

		isSingleProductPriceValid() {
			const sellingPrice = parseFloat(this.priceConfig.sellingPrice) || 0;
			const recommendedPrice = getMarketplaceFinalPrice(this.product, [], 1);
			return sellingPrice > 0 && sellingPrice >= recommendedPrice;
		},

		applyBulkPrice() {
			if (!this.priceConfig.bulkPrice || !this.product.variations || !this.product.variations.prices) return;

			const bulkPrice = parseFloat(this.priceConfig.bulkPrice.replace(/[^\d.]/g, ''));
			this.product.variations.prices.forEach((variation) => {
				this.$set(variation, 'selling_price', bulkPrice);
			});

			this.$notify({
				group: 'app',
				title: 'Success',
				text: `Berhasil menerapkan harga Rp ${formatMoney(bulkPrice)} ke semua variasi`,
				type: 'success',
				duration: 3000,
			});
		},
		async loadProduct() {
			if (!this.currentProductSlug) {
				this.error = 'Product slug tidak ditemukan';
				return;
			}

			this.isLoading = true;
			this.error = null;

			try {
				const response = await new Promise((resolve, reject) => {
					productsApi.getPublicProducts(
						{
							slug: this.currentProductSlug,
							limit: 1,
							page: 1,
							visibility: 'all',
						},
						response => resolve(response),
						error => reject(error),
					);
				});
				if (response.data && response.data && response.data.products.length > 0) {
					this.product = response.data.products[0];
				} else {
					this.error = 'Produk tidak ditemukan';
				}
			} catch (error) {
				this.error = 'Gagal memuat data produk. Silakan coba lagi.';
			} finally {
				this.isLoading = false;
			}
		},

		async fetchProductAddedStatus() {
			const productId = this.getProductId();
			if (!productId) {
				return;
			}

			this.isFetchingProductStatus = true;

			try {
				const response = await new Promise((resolve, reject) => {
					productsApi.getProductAddedStatus(
						productId,
						response => resolve(response),
						error => reject(error),
					);
				});
				this.productAddedStatus = response.data;
			} catch (error) {
				console.error('Error fetching product added status:', error);
				this.productAddedStatus = null;
			} finally {
				this.isFetchingProductStatus = false;
			}
		},

		async fetchShops() {
			if (!this.product || !this.product.user) {
				return;
			}

			this.isFetchingShops = true;

			try {
				const response = await new Promise((resolve, reject) => {
					checkSupplier(
						this.product.user._id.$oid,
						{ refetch_ids: '' },
						response => resolve(response),
						error => reject(error),
					);
				});

				const shops = response.data?.data || [];
				this.supplierAddress = response.data.supplier;
				// Sort shops: available shops first, then disabled shops
				this.shopeeShops = shops.sort((a, b) => {
					const aAvailable = this.isShopAvailable(a);
					const bAvailable = this.isShopAvailable(b);

					// If both shops have same availability status, maintain original order
					if (aAvailable === bAvailable) {
						return 0;
					}

					// Available shops (true) come first, disabled shops (false) come last
					return bAvailable - aAvailable;
				});
			} catch (error) {
				console.error('Error fetching shops:', error);
			} finally {
				this.isFetchingShops = false;
			}
		},

		async refreshShops() {
			// Store the currently selected shops to reselect them after refresh
			const currentSelectedShops = [...this.selectedShopIds];

			this.isFetchingShops = true;

			try {
				const response = await new Promise((resolve, reject) => {
					checkSupplier(
						this.product.user._id.$oid,
						{ refetch_ids: currentSelectedShops.join(',') },
						response => resolve(response),
						error => reject(error),
					);
				});

				const shops = response.data?.data || [];
				this.supplierAddress = response.data.supplier;
				// Sort shops: available shops first, then disabled shops
				this.shopeeShops = shops.sort((a, b) => {
					const aAvailable = this.isShopAvailable(a);
					const bAvailable = this.isShopAvailable(b);

					// If both shops have same availability status, maintain original order
					if (aAvailable === bAvailable) {
						return 0;
					}

					// Available shops (true) come first, disabled shops (false) come last
					return bAvailable - aAvailable;
				});

				// Reselect the previously selected shops after data is loaded
				this.selectedShopIds = currentSelectedShops;
			} catch (error) {
				console.error('Error fetching shops:', error);
			} finally {
				this.isFetchingShops = false;
			}
		},

		async submitProduct() {
			if (!this.canSubmit) {
				return;
			}

			// Tampilkan modal loading
			this.showLoadingModal = true;

			const variations = { ...this.productVariations };
			variations.prices = variations.prices.map((price) => {
				price.price = parseInt(price.selling_price);
				delete price.selling_price;
				return price;
			});
			const payload = {
				product_id: this.getProductId(),
				shop_ids: this.selectedShopIds,
				selling_price: this.priceConfig.sellingPrice,
				variations,
			};

			this.isSubmitting = true;

			try {
				const response = await new Promise((resolve, reject) => {
					addProduct(
						payload,
						response => resolve(response),
						error => reject(error),
					);
				});

				// Success - tampilkan notifikasi dan biarkan user menutup modal
				this.$notify({
					group: 'app',
					title: 'Berhasil',
					text: 'Produk berhasil ditambahkan ke marketplace',
					type: 'success',
				});

				console.log(response);
			} catch (error) {
				console.error('Error submitting product:', error);
				this.$notify({
					group: 'app',
					title: 'Error',
					text: 'Gagal menambahkan produk ke marketplace',
					type: 'error',
				});
				// Close modal on error
				this.showLoadingModal = false;
			} finally {
				this.isSubmitting = false;
			}
		},

		closeLoadingModal() {
			this.showLoadingModal = false;
			// Optional: redirect atau refresh setelah modal ditutup
			// this.$router.go(0);
		},
		copyAddress(address) {
			if (!address) return;

			// Copy to clipboard
			navigator.clipboard.writeText(address).then(() => {
				// Show success notification
				this.$notify({
					group: 'app',
					title: 'Success',
					text: 'Alamat berhasil disalin ke clipboard',
					type: 'success',
					duration: 2000,
				});
			}).catch((error) => {
				console.error('Failed to copy address:', error);
				// Show error notification
				this.$notify({
					group: 'app',
					title: 'Error',
					text: 'Gagal menyalin alamat',
					type: 'error',
					duration: 3000,
				});
			});
		},

		getProductId() {
			if (!this.product) {
				return '';
			}
			let productId = this.product._id;
			if (typeof this.product._id === 'object') {
				productId = this.product._id.$oid;
			}
			return productId;
		},

		getProductUserId() {
			if (!this.product || !this.product.user) {
				return null;
			}
			let userId = this.product.user._id;
			if (typeof this.product.user._id === 'object') {
				userId = this.product.user._id.$oid;
			}
			return userId;
		},

		isShopAvailable(shop) {
			// Check if shop is already added for this product
			if (this.isShopAlreadyAdded(shop)) {
				return false;
			}

			// Get product user ID
			const productUserId = this.getProductUserId();

			if (!productUserId) {
				// If no product user ID, shop is available only if it doesn't have supplier_id
				return !shop.supplier_id;
			}

			// Shop is available if:
			// 1. It doesn't have supplier_id (available to all)
			// 2. It has supplier_id that matches the product's user._id
			return !shop.supplier_id || shop.supplier_id === productUserId;
		},

		isShopAlreadyAdded(shop) {
			// Check if this shop_id exists in the added Shopee shop IDs
			return this.addedShopeeShopIds.includes(shop.shop_id);
		},

		goToMarketplaceSettings() {
			this.$router.push('/settings/marketplace');
		},

		formatMarketplacePrice(product) {
			// console.log('product: ', product);
			const price = getMarketplaceFinalPrice(product, [], 1) ?? 0;
			return formatMoney(price);
		},
	},
};
</script>

<style scoped>
.container {
	max-width: 1200px;
	margin: 0 auto;
	padding: 2rem;
}

.page {
	padding: 1rem 0;
}

.shops-list {
	display: flex;
	flex-direction: column;
	gap: 0.75rem;
	max-height: 400px;
	overflow-y: auto;
	padding: 0.5rem 0;
}

.shop-icon {
	color: #fbaf5d;
	font-size: 1.7rem;
}

.shop-item {
	display: flex;
	align-items: center;
	padding: 1rem;
	border: 1px solid #dee2e6;
	border-radius: 8px;
	background-color: #fff;
	transition: all 0.2s ease-in-out;
}

.shop-item:hover:not(.shop-item--disabled) {
	border-color: #0d6efd;
	box-shadow: 0 2px 8px rgba(13, 110, 253, 0.08);
}

.shop-item__checkbox {
	margin-right: 1rem;
}

.shop-item__checkbox input[type="checkbox"] {
	width: 18px;
	height: 18px;
	cursor: pointer;
}

.shop-item__content {
	flex: 1;
	cursor: pointer;
	margin: 0;
}

.shop-item__info {
	display: flex;
	flex-direction: column;
	gap: 0.25rem;
}

.shop-item__body {
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 0.5rem;
}

.shop-item__name {
	font-weight: 600;
	color: #212529;
	flex: 1;
}

.shop-item--disabled {
	opacity: 0.6;
	background-color: #f8f9fa;
	cursor: not-allowed;
}

.shop-item--disabled:hover {
	border-color: #dee2e6;
	box-shadow: none;
}

.shop-item--disabled .shop-item__content {
	cursor: not-allowed;
}

.store-selection__empty {
	padding: 1.5rem;
	text-align: center;
	border: 1px dashed #d0d7de;
	border-radius: 12px;
	background-color: #fafbfc;
}

.store-selection__empty p {
	margin-bottom: 1rem;
	color: #6c757d;
}

.shop-actions {
	display: flex;
	flex-direction: column;
	gap: 0.5rem;
}

.shop-actions .btn {
	padding: 0.75rem 1rem;
	font-size: 0.9rem;
	font-weight: 600;
}

.supplier-address-warning {
	margin-top: 1rem;
}

.supplier-address-warning__header {
	display: flex;
	align-items: center;
	gap: 0.5rem;
	font-weight: 600;
	color: #856404;
	margin-bottom: 0.75rem;
}

.supplier-address-warning__header .material-icons {
	color: #ffc107;
	font-size: 1.25rem;
}

.supplier-address-warning__content {
	background-color: #fffdf5;
	border: 1px solid #ffc107;
	border-radius: 8px;
	padding: 1rem;
}

.supplier-address-warning__content p {
	margin-bottom: 0.75rem;
	color: #495057;
}

.supplier-address-warning__content p:last-child {
	margin-bottom: 0;
}

.supplier-address-details {
	background-color: #e3eefd;
	border: 1px solid var(--info);
	border-radius: 6px;
	padding: 0.75rem;
	margin-bottom: 0.75rem;
}

.supplier-address-row {
	display: flex;
	margin-bottom: 0.5rem;
}

.supplier-address-row:last-child {
	margin-bottom: 0;
}

.supplier-address-row strong {
	min-width: 100px;
	color: #212529;
}

.supplier-address-note {
	font-size: 0.875rem;
	font-style: italic;
	color: #6c757d;
}

.supplier-address__copy-container {
	display: flex;
	align-items: center;
	gap: 0.5rem;
	flex: 1;
}

.supplier-address__address {
	flex: 1;
	word-break: break-word;
}

.supplier-address__copy {
	padding: 0.25rem 0.5rem;
	font-size: 0.75rem;
	display: flex;
	align-items: center;
	gap: 0.25rem;
	flex-shrink: 0;
}

.supplier-address__copy .material-icons {
	font-size: 0.875rem;
}

.supplier-address__copy:hover {
	opacity: 0.8;
}

/* Small spinner for buttons */
.spinner-border-sm {
	width: 1rem;
	height: 1rem;
	border-width: 0.2em;
}

/* Spinning icon animation */
.material-icons.spin {
	animation: spin 1s linear infinite;
}

@keyframes spin {
	0% {
		transform: rotate(0deg);
	}

	100% {
		transform: rotate(360deg);
	}
}

.btn.is-loading {
	position: relative;
}

.wizard-steps {
	padding: 1rem 0;
	max-width: 600px;
	margin: 0 auto;
}

.step-item {
	position: relative;
}

.step-circle {
	width: 40px;
	height: 40px;
	border-radius: 50%;
	background-color: #e9ecef;
	color: #6c757d;
	display: flex;
	align-items: center;
	justify-content: center;
	font-weight: 600;
	font-size: 0.875rem;
	border: 2px solid #e9ecef;
}

.step-item.active .step-circle {
	background-color: #007bff;
	color: white;
	border-color: #007bff;
}

.step-item.completed .step-circle {
	background-color: #28a745;
	color: white;
	border-color: #28a745;
}

.step-label {
	font-size: 0.875rem;
	color: #6c757d;
}

.step-item.active .step-label {
	color: #007bff;
}

.step-item.completed .step-label {
	color: #28a745;
}

.step-connector {
	height: 2px;
	background-color: #e9ecef;
	position: relative;
	margin: 0 10px;
	align-self: center;
	margin-top: -25px;
}

.step-item.completed + .step-connector {
	background-color: #28a745;
}

</style>
